import type { AppLocale } from '../i18n';
import { courseTitleFromFlowContext, FLOW_CONTEXT_PATH_SPLIT, pickFlowItemTitle } from './catalogFlowCourse';
import type { LessonMapData, LessonMapItem, LessonMapSection } from './lessonMapSections';

export interface CatalogFlowMapSourceItem {
  kind: string;
  entityId: number;
  courseId: number;
  levelId?: number | null;
  lessonId?: number | null;
  title: string;
  titleFa?: string | null;
  contextSubtitle: string;
  contextSubtitleFa?: string | null;
  isCompleted: boolean;
  isLocked?: boolean;
  subscriptionPlanIds?: number[];
}

export interface BuildCatalogFlowMapOptions {
  locale: AppLocale;
  /** `${kind}-${entityId}` of the active / highlighted row. */
  currentKey?: string | null;
  courseFilter?: number | null;
  mapTitle: string;
}

function contextParts(item: CatalogFlowMapSourceItem, locale: AppLocale): string[] {
  const primary =
    locale === 'fa' && item.contextSubtitleFa?.trim()
      ? item.contextSubtitleFa.trim()
      : item.contextSubtitle.trim();
  if (!primary) return [];
  return primary.split(FLOW_CONTEXT_PATH_SPLIT).map((p) => p.trim()).filter(Boolean);
}

/** Short label for the item card: lesson / level / course title only. */
function scopeTitleForItem(item: CatalogFlowMapSourceItem, locale: AppLocale): string {
  const parts = contextParts(item, locale);
  const kind = (item.kind || '').toLowerCase();
  if (kind === 'lesson') {
    return parts[2] || parts[parts.length - 1] || pickFlowItemTitle(item, locale);
  }
  if (kind === 'level') {
    return parts[1] || parts[parts.length - 1] || pickFlowItemTitle(item, locale);
  }
  return (
    parts[0] ||
    courseTitleFromFlowContext(item.contextSubtitle, item.courseId, item.contextSubtitleFa, locale)
  );
}

function courseTitleForItem(item: CatalogFlowMapSourceItem, locale: AppLocale): string {
  const parts = contextParts(item, locale);
  return (
    parts[0] ||
    courseTitleFromFlowContext(item.contextSubtitle, item.courseId, item.contextSubtitleFa, locale)
  );
}

function levelTitleForItem(item: CatalogFlowMapSourceItem, locale: AppLocale): string {
  const parts = contextParts(item, locale);
  if (parts[1]) return parts[1];
  return scopeTitleForItem(item, locale);
}

function lessonTitleForItem(item: CatalogFlowMapSourceItem, locale: AppLocale): string {
  const parts = contextParts(item, locale);
  if (parts[2]) return parts[2];
  return scopeTitleForItem(item, locale);
}

function toMapItem(
  item: CatalogFlowMapSourceItem,
  order: number,
  currentKey: string | null,
  locale: AppLocale
): LessonMapItem {
  const itemKey = `${item.kind}-${item.entityId}`;
  return {
    id: itemKey,
    kind: 'lesson',
    entityId: item.entityId,
    // Quiz/exercise name only — course/level/lesson are shown as nested section headers above.
    title: pickFlowItemTitle(item, locale),
    order,
    isCompleted: item.isCompleted,
    isCurrent: currentKey != null && currentKey === itemKey,
    isLocked: item.isLocked === true,
    ...(item.subscriptionPlanIds?.length ? { subscriptionPlanIds: item.subscriptionPlanIds } : {}),
    flowKind: item.kind
  };
}

type LessonBucket = { title: string; items: CatalogFlowMapSourceItem[] };
type LevelBucket = {
  title: string;
  levelItems: CatalogFlowMapSourceItem[];
  lessons: Map<number, LessonBucket>;
};
type CourseBucket = {
  title: string;
  courseItems: CatalogFlowMapSourceItem[];
  levels: Map<number, LevelBucket>;
};

/**
 * Nested catalog map: Course → Level → Lesson, with short scope labels on each item.
 */
export function buildCatalogFlowMap(
  items: CatalogFlowMapSourceItem[],
  options: BuildCatalogFlowMapOptions
): LessonMapData {
  const { locale, currentKey = null, courseFilter = null, mapTitle } = options;

  const scoped =
    courseFilter != null && courseFilter > 0
      ? items.filter((i) => i.courseId === courseFilter)
      : items.filter((i) => i.courseId > 0);

  const courses = new Map<number, CourseBucket>();

  for (const item of scoped) {
    let course = courses.get(item.courseId);
    if (!course) {
      course = {
        title: courseTitleForItem(item, locale),
        courseItems: [],
        levels: new Map()
      };
      courses.set(item.courseId, course);
    }

    const kind = (item.kind || '').toLowerCase();

    if (kind === 'course' || !item.levelId) {
      course.courseItems.push(item);
      continue;
    }

    const levelId = item.levelId;
    let level = course.levels.get(levelId);
    if (!level) {
      level = {
        title: levelTitleForItem(item, locale),
        levelItems: [],
        lessons: new Map()
      };
      course.levels.set(levelId, level);
    }

    if (kind === 'level' || !item.lessonId) {
      level.levelItems.push(item);
      continue;
    }

    const lessonId = item.lessonId;
    let lesson = level.lessons.get(lessonId);
    if (!lesson) {
      lesson = { title: lessonTitleForItem(item, locale), items: [] };
      level.lessons.set(lessonId, lesson);
    }
    lesson.items.push(item);
  }

  const sections: LessonMapSection[] = [];
  let order = 0;

  for (const [courseId, course] of courses) {
    const courseChildren: LessonMapSection[] = [];
    const courseDirectItems: LessonMapItem[] = [];

    for (const item of course.courseItems) {
      order += 1;
      courseDirectItems.push(toMapItem(item, order, currentKey, locale));
    }

    for (const [levelId, level] of course.levels) {
      const levelChildren: LessonMapSection[] = [];
      const levelDirectItems: LessonMapItem[] = [];

      for (const item of level.levelItems) {
        order += 1;
        levelDirectItems.push(toMapItem(item, order, currentKey, locale));
      }

      for (const [lessonId, lesson] of level.lessons) {
        const lessonItems: LessonMapItem[] = lesson.items.map((item) => {
          order += 1;
          return toMapItem(item, order, currentKey, locale);
        });
        if (lessonItems.length === 0) continue;
        levelChildren.push({
          levelId: lessonId,
          levelTitle: lesson.title,
          sectionKind: 'lesson',
          levelCompleted: lessonItems.every((i) => i.isCompleted),
          items: lessonItems
        });
      }

      if (levelDirectItems.length === 0 && levelChildren.length === 0) continue;

      const levelAllItems = [
        ...levelDirectItems,
        ...levelChildren.flatMap((c) => c.items)
      ];
      courseChildren.push({
        levelId,
        levelTitle: level.title,
        sectionKind: 'level',
        levelCompleted: levelAllItems.length > 0 && levelAllItems.every((i) => i.isCompleted),
        items: levelDirectItems,
        ...(levelChildren.length > 0 ? { children: levelChildren } : {})
      });
    }

    if (courseDirectItems.length === 0 && courseChildren.length === 0) continue;

    const courseAllItems = [
      ...courseDirectItems,
      ...courseChildren.flatMap((c) => [
        ...c.items,
        ...(c.children ?? []).flatMap((ch) => ch.items)
      ])
    ];

    sections.push({
      levelId: courseId,
      levelTitle: course.title,
      sectionKind: 'course',
      levelCompleted: courseAllItems.length > 0 && courseAllItems.every((i) => i.isCompleted),
      items: courseDirectItems,
      ...(courseChildren.length > 0 ? { children: courseChildren } : {})
    });
  }

  const courseIds = new Set(scoped.map((i) => i.courseId));
  const subtitle =
    courseIds.size === 1 && scoped[0]
      ? courseTitleFromFlowContext(
          scoped[0].contextSubtitle,
          scoped[0].courseId,
          scoped[0].contextSubtitleFa,
          locale
        )
      : undefined;

  return {
    title: mapTitle,
    ...(subtitle ? { subtitle } : {}),
    sections
  };
}
